import { motion } from 'framer-motion';
import { Check, Lock, Sparkles } from 'lucide-react';
import type { SkillAnotada } from '@/engine/skills';
import { cn } from '@/utils/cn';

interface Props {
  anotada: SkillAnotada;
  onClick: () => void;
}

const RAMA_COLOR: Record<string, { border: string; glow: string; text: string }> = {
  guardian: {
    border: 'border-ember-700',
    glow: 'shadow-[0_0_14px_rgba(237,95,30,0.35)]',
    text: 'text-ember-300',
  },
  druida: {
    border: 'border-moss-600',
    glow: 'shadow-[0_0_14px_rgba(92,145,73,0.35)]',
    text: 'text-moss-300',
  },
  estratega: {
    border: 'border-mystic-500',
    glow: 'shadow-[0_0_14px_rgba(115,123,192,0.35)]',
    text: 'text-mystic-300',
  },
};

export function SkillNode({ anotada, onClick }: Props) {
  const { skill, estado, motivo, fog } = anotada;
  const color = RAMA_COLOR[skill.rama];
  const desbloqueada = estado === 'desbloqueada';
  const disponible = estado === 'disponible';
  const enNiebla = fog === 'niebla';
  const esSpoiler = fog === 'spoiler';

  return (
    <motion.button
      type="button"
      onClick={disponible ? onClick : undefined}
      disabled={!disponible}
      className={cn(
        'relative w-full rounded-md border-2 p-3 text-left transition-all',
        'bg-ash-950/70 backdrop-blur-sm',
        desbloqueada && `${color.border} ${color.glow}`,
        disponible && `${color.border} cursor-pointer`,
        !desbloqueada && !disponible && 'border-ash-800 opacity-55 cursor-not-allowed',
        enNiebla && '[&_*]:blur-[2px]',
        esSpoiler && '[&_*]:blur-[5px] opacity-40',
      )}
      whileHover={disponible ? { scale: 1.01 } : undefined}
      whileTap={disponible ? { scale: 0.99 } : undefined}
      title={esSpoiler ? 'Spoiler: no revelado aún.' : undefined}
    >
      {/* Overlay para spoilers: muestra un "???" sin blur encima del contenido */}
      {esSpoiler && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
          <span className="font-display text-ash-300 fluid-lg tracking-widest">???</span>
        </div>
      )}

      <header className="flex items-start gap-2 mb-1">
        <div
          className={cn(
            'mt-0.5 shrink-0',
            desbloqueada ? color.text : 'text-ash-500',
          )}
        >
          {desbloqueada ? (
            <Check className="w-3.5 h-3.5" />
          ) : disponible ? (
            <Sparkles className="w-3.5 h-3.5 animate-glow-pulse" />
          ) : (
            <Lock className="w-3.5 h-3.5" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="fluid-xs uppercase tracking-wider text-ash-500 font-ui truncate">
            T{skill.tier} · {skill.rama}
          </p>
          <h3
            className={cn(
              'font-display fluid-sm',
              desbloqueada ? color.text : 'text-ash-200',
            )}
          >
            {skill.nombre}
          </h3>
        </div>
        {!desbloqueada && (
          <span className="fluid-xs font-ui text-ash-500 tabular-nums shrink-0">
            {skill.costo}pt
          </span>
        )}
      </header>

      <p className="fluid-xs text-ash-400 leading-snug clamp-3">{skill.descripcion}</p>

      {motivo && !desbloqueada && (
        <p className="mt-1 fluid-xs text-ash-600 italic font-ui clamp-2">{motivo}</p>
      )}
    </motion.button>
  );
}
